import React, { useEffect, useState } from 'react';
import {
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Alert,
  Snackbar
} from '@mui/material';
import errorEntryService, { ErrorEntry } from '../services/errorEntryService';
import authService from '../services/authService';

const ErrorTracking: React.FC = () => {
  const [errorEntries, setErrorEntries] = useState<ErrorEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [currentErrorEntry, setCurrentErrorEntry] = useState<ErrorEntry | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const currentUser = authService.getCurrentUser();
  const currentUserRole = currentUser?.role || '';
  const currentUserDepartment = currentUser?.department || '';

  useEffect(() => {
    fetchErrorEntries();
  }, []);

  const fetchErrorEntries = async () => {
    try {
      setLoading(true);
      const fetchedEntries = await errorEntryService.getAll();
      setErrorEntries(fetchedEntries);
      setError(null);
    } catch (err) {
      setError('Failed to fetch error entries');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (entry: ErrorEntry | null = null) => {
    if (entry) {
      // Employees can only edit their own entries
      if (currentUserRole === 'Employee' && entry.user_id !== currentUser?.id) {
        setSnackbar({ open: true, message: 'You can only edit your own error entries', severity: 'error' });
        return;
      }
      // Managers can only edit entries in their department
      if (currentUserRole === 'Manager' && entry.department !== currentUserDepartment) {
        setSnackbar({ open: true, message: 'You can only edit error entries in your department', severity: 'error' });
        return;
      }
      // Only Admin can edit/delete error entries (as per requirements)
      if (currentUserRole !== 'Admin') {
        setSnackbar({ open: true, message: 'Only administrators can edit error entries', severity: 'error' });
        return;
      }
    }
    setCurrentErrorEntry(entry);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentErrorEntry(null);
  };

  const handleSave = async () => {
    if (!currentErrorEntry || !currentUser) return;

    try {
      let savedEntry: ErrorEntry;
      
      if (currentErrorEntry.id) {
        // Update existing entry
        // Only Admin can update error entries
        if (currentUserRole !== 'Admin') {
          setSnackbar({ open: true, message: 'Only administrators can update error entries', severity: 'error' });
          return;
        }
        
        savedEntry = await errorEntryService.update(currentErrorEntry.id, {
          ...currentErrorEntry
        });
        setErrorEntries(errorEntries.map(e => e.id === savedEntry.id ? savedEntry : e));
        setSnackbar({ open: true, message: 'Error entry updated successfully', severity: 'success' });
      } else {
        // Create new entry
        savedEntry = await errorEntryService.create({
          ...currentErrorEntry
        });
        setErrorEntries([...errorEntries, savedEntry]);
        setSnackbar({ open: true, message: 'Error entry created successfully', severity: 'success' });
      }
      handleClose();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save error entry', severity: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    // Only Admin can delete error entries (as per requirements)
    if (currentUserRole !== 'Admin') {
      setSnackbar({ open: true, message: 'Only administrators can delete error entries', severity: 'error' });
      return;
    }

    try {
      await errorEntryService.delete(id);
      setErrorEntries(errorEntries.filter(e => e.id !== id));
      setSnackbar({ open: true, message: 'Error entry deleted successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete error entry', severity: 'error' });
    }
  };

  const handleChange = (field: keyof ErrorEntry, value: string | number | null) => {
    if (currentErrorEntry) {
      setCurrentErrorEntry({ ...currentErrorEntry, [field]: value });
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Typography color="error">{error}</Typography>
      </div>
    );
  }

  // Only Admin can manage error entries (as per requirements)
  if (currentUserRole !== 'Admin') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Typography color="error">Access denied. Only administrators can manage error entries.</Typography>
      </div>
    );
  }

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Error Tracking
        </Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpen()}>
          Add Error Entry
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User ID</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Error Type</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {errorEntries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{entry.user_id}</TableCell>
                  <TableCell>{entry.department}</TableCell>
                  <TableCell>{entry.error_type}</TableCell>
                  <TableCell>{entry.notes || '-'}</TableCell>
                  <TableCell>
                    <Button onClick={() => handleOpen(entry)} size="small">Edit</Button>
                    <Button onClick={() => handleDelete(entry.id)} color="error" size="small">Delete</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{currentErrorEntry?.id ? 'Edit Error Entry' : 'Add Error Entry'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="User ID"
            fullWidth
            type="number"
            value={currentErrorEntry?.user_id || currentUser?.id || ''}
            onChange={(e) => handleChange('user_id', parseInt(e.target.value))}
          />
          <TextField
            margin="dense"
            label="Department"
            fullWidth
            value={currentErrorEntry?.department || currentUserDepartment || ''}
            onChange={(e) => handleChange('department', e.target.value)}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Error Type</InputLabel>
            <Select
              value={currentErrorEntry?.error_type || ''}
              onChange={(e) => handleChange('error_type', e.target.value)}
            >
              <MenuItem value="Data Entry Error">Data Entry Error</MenuItem>
              <MenuItem value="System Error">System Error</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Notes"
            fullWidth
            multiline
            rows={4}
            value={currentErrorEntry?.notes || ''}
            onChange={(e) => handleChange('notes', e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {currentErrorEntry?.id ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ErrorTracking;